/**
 * currencyGate — Free-tier cross-currency conversion gating.
 *
 * Free users get cross-currency conversion for subscriptions that match
 * their auto-detected "primary currency" (the most common currency among
 * their own subscriptions — NOT the arbitrary displayCurrency preference,
 * which could otherwise zero out an all-foreign-currency user's totals).
 * Pro users are unaffected — everything converts as today.
 */
import { type DatabaseSubscription } from "@/hooks/useSubscriptions";

/**
 * Most common currency among the given subscriptions. Ties are broken by
 * the oldest subscription's currency (deterministic, no user-facing impact).
 * Returns null if there are no subscriptions.
 */
export function getPrimaryCurrency(
  subscriptions: DatabaseSubscription[],
): string | null {
  if (subscriptions.length === 0) return null;

  const counts = new Map<string, number>();
  for (const sub of subscriptions) {
    counts.set(sub.currency, (counts.get(sub.currency) ?? 0) + 1);
  }

  const maxCount = Math.max(...counts.values());
  const tiedCurrencies = [...counts.entries()]
    .filter(([, count]) => count === maxCount)
    .map(([currency]) => currency);

  if (tiedCurrencies.length === 1) return tiedCurrencies[0];

  // Tie-break: currency of the oldest subscription among the tied currencies.
  const oldest = [...subscriptions]
    .filter((sub) => tiedCurrencies.includes(sub.currency))
    .sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    )[0];

  return oldest.currency;
}

export interface FreeTierCurrencyFilter {
  primaryCurrency: string | null;
  included: DatabaseSubscription[];
  excluded: DatabaseSubscription[];
}

/**
 * Splits subscriptions into "included" (counted in totals) and "excluded"
 * (shown in their own currency, but not summed) for Free users. Pro users
 * get everything included, unchanged.
 */
export function filterIncludedForFreeTier(
  subscriptions: DatabaseSubscription[],
  isPro: boolean,
): FreeTierCurrencyFilter {
  if (isPro) {
    return { primaryCurrency: null, included: subscriptions, excluded: [] };
  }

  const primaryCurrency = getPrimaryCurrency(subscriptions);
  const included = subscriptions.filter(
    (sub) => sub.currency === primaryCurrency,
  );
  const excluded = subscriptions.filter(
    (sub) => sub.currency !== primaryCurrency,
  );

  return { primaryCurrency, included, excluded };
}
